import {isArray} from 'lodash';

export type BaseApiErrorProps<T extends string | undefined> = {
    data?: T extends string ? {[key in T]: string[]} : never;
    message: string;
    status: number;
};

type ErrorType = {
    message: string;
    type: 'validation';
};

type HatsFieldError = {code: string; message: string}[] | string[] | string[][];

type UiFieldError<T> = {
    key: T;
    value: ErrorType;
};

const getFieldErrorMessage = (input: HatsFieldError): [string] | undefined => {
    if (typeof input[0] === 'string') {
        return [input[0]];
    }

    if (isArray(input[0]) && typeof input[0][0] === 'string') {
        return [input[0][0]];
    }

    if ('message' in input[0]) {
        return [input[0].message as string];
    }

    return undefined;
};

export default class ApiError<T extends string | undefined> extends Error {
    readonly data;

    readonly fieldErrors: Array<UiFieldError<'nonFieldErrors' | T>> = [];

    readonly message: string;

    readonly status: number;

    constructor(props: BaseApiErrorProps<'nonFieldErrors' | T>) {
        super(props.message);

        if (props.data) {
            this.data = Object.fromEntries(
                Object.entries(props.data).map(([key, value]) => [
                    key,
                    getFieldErrorMessage(value),
                ]),
            );
        }
        this.message = props.message;
        this.status = props.status;

        if (this.data) {
            this.fieldErrors = Object.entries(this.data)
                .map(([key, value]) => ({
                    key,
                    value: {
                        message: value,
                        type: 'validation',
                    },
                }))
                .filter((fieldError) => fieldError.value) as unknown as Array<
                UiFieldError<'nonFieldErrors' | T>
            >;
        } else {
            this.fieldErrors = [
                {
                    key: 'nonFieldErrors',
                    value: {
                        message: props.message,
                        type: 'validation',
                    },
                },
            ] as Array<UiFieldError<'nonFieldErrors' | T>>;
        }
    }

    public getFieldError = (
        targetKey: 'nonFieldErrors' | T,
    ): UiFieldError<'nonFieldErrors' | T> | undefined =>
        this.fieldErrors.find(({key}) => key === targetKey);
}
